import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flag, Kanban, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { reportUiError } from '../../sentry';
import {
  isOpenTaskStatus,
  slugifyEpicTitle,
  STRATEGY_SCORE_LABELS,
  WORK_EPIC_STATUSES,
  type NetworkingTaskStatus,
  type WorkEpicRow,
  type WorkEpicStatus,
} from '../../lib/networkingTasks';

type TaskCountRow = {
  epic_id: string | null;
  status: NetworkingTaskStatus;
};

type EpicWithCounts = WorkEpicRow & {
  openCount: number;
  doneCount: number;
  totalCount: number;
};

type EditField = 'title' | 'description' | 'deadline' | 'value_note';

function formatDeadline(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function isDeadlinePast(date: string | null): boolean {
  if (!date) return false;
  const d = new Date(`${date}T23:59:59`);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

function toDateInputValue(date: string | null): string {
  if (!date) return '';
  const m = date.match(/^(\d{4}-\d{2}-\d{2})/);
  return m?.[1] ?? '';
}

const STATUS_PILL: Record<WorkEpicRow['status'], string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
  paused: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200',
  done: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

const inputClass =
  'w-full px-2.5 py-1.5 border border-indigo-300 dark:border-indigo-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

const WorkEpicsView: React.FC = () => {
  const [epics, setEpics] = useState<WorkEpicRow[]>([]);
  const [counts, setCounts] = useState<TaskCountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<EditField | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createDeadline, setCreateDeadline] = useState('');
  const [createScore, setCreateScore] = useState<number | ''>('');
  const [createValueNote, setCreateValueNote] = useState('');
  const [live, setLive] = useState(false);
  const skipBlurSave = useRef(false);
  const editingRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);

  const fetchEpics = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const [epicsRes, tasksRes] = await Promise.all([
        supabase.from('work_epics').select('*').order('sort_order', { ascending: true }),
        supabase.from('tasks').select('epic_id, status'),
      ]);
      if (epicsRes.error) throw epicsRes.error;
      if (tasksRes.error) throw tasksRes.error;
      setEpics((epicsRes.data ?? []) as WorkEpicRow[]);
      setCounts((tasksRes.data ?? []) as TaskCountRow[]);
    } catch (e: unknown) {
      if (!opts?.silent) {
        toast.error(e instanceof Error ? e.message : 'Failed to load epics');
        setEpics([]);
        setCounts([]);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchEpics();
  }, [fetchEpics]);

  editingRef.current = Boolean(editingId || saving || creating);

  useEffect(() => {
    const scheduleRefresh = () => {
      if (editingRef.current) {
        pendingRefreshRef.current = true;
        return;
      }
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => {
        void fetchEpics({ silent: true });
      }, 250);
    };

    const channel = supabase
      .channel('work-epics-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'work_epics' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, scheduleRefresh)
      .subscribe((status) => {
        setLive(status === 'SUBSCRIBED');
      });

    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      void supabase.removeChannel(channel);
    };
  }, [fetchEpics]);

  useEffect(() => {
    if (editingId || saving || creating || !pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    void fetchEpics({ silent: true });
  }, [editingId, saving, creating, fetchEpics]);

  const rows: EpicWithCounts[] = useMemo(() => {
    const byEpic = new Map<string, { open: number; done: number; total: number }>();
    for (const t of counts) {
      if (!t.epic_id) continue;
      const cur = byEpic.get(t.epic_id) ?? { open: 0, done: 0, total: 0 };
      cur.total += 1;
      if (isOpenTaskStatus(t.status)) cur.open += 1;
      if (t.status === 'done') cur.done += 1;
      byEpic.set(t.epic_id, cur);
    }

    return epics
      .filter((e) => showInactive || e.status === 'active' || e.status === 'paused')
      .map((e) => {
        const c = byEpic.get(e.id) ?? { open: 0, done: 0, total: 0 };
        return {
          ...e,
          openCount: c.open,
          doneCount: c.done,
          totalCount: c.total,
        };
      });
  }, [epics, counts, showInactive]);

  const unplacedOpen = useMemo(
    () => counts.filter((t) => !t.epic_id && isOpenTaskStatus(t.status)).length,
    [counts],
  );

  const beginEdit = (epic: WorkEpicRow, field: EditField) => {
    setEditingId(epic.id);
    setEditingField(field);
    if (field === 'title') setDraft(epic.title);
    else if (field === 'description') setDraft(epic.description ?? '');
    else if (field === 'value_note') setDraft(epic.value_note ?? '');
    else setDraft(toDateInputValue(epic.target_date));
    skipBlurSave.current = false;
  };

  const cancelEdit = () => {
    skipBlurSave.current = true;
    setEditingId(null);
    setEditingField(null);
    setDraft('');
  };

  const resetCreateForm = () => {
    setCreateOpen(false);
    setCreateTitle('');
    setCreateDescription('');
    setCreateDeadline('');
    setCreateScore('');
    setCreateValueNote('');
  };

  const submitCreate = async () => {
    const title = createTitle.trim();
    if (!title) {
      toast.error('Give the epic a plan name.');
      return;
    }
    if (creating) return;
    setCreating(true);
    let slug = slugifyEpicTitle(title);
    const maxSort = epics.reduce((m, e) => Math.max(m, e.sort_order ?? 0), 0);
    const payload = {
      slug,
      title,
      description: createDescription.trim() || null,
      target_date: createDeadline.trim() || null,
      status: 'active' as const,
      strategy_score: createScore === '' ? null : createScore,
      value_note: createValueNote.trim() || null,
      sort_order: maxSort > 0 ? maxSort + 10 : 50,
    };
    let { data, error } = await supabase.from('work_epics').insert(payload).select('*').single();
    if (error?.code === '23505') {
      slug = `${slug}-${Date.now().toString(36).slice(-4)}`.slice(0, 48);
      ({ data, error } = await supabase
        .from('work_epics')
        .insert({ ...payload, slug })
        .select('*')
        .single());
    }
    setCreating(false);
    if (error) {
      reportUiError('tasks', 'create_work_epic', error);
      toast.error(error.message || 'Failed to create epic');
      return;
    }
    const row = data as WorkEpicRow;
    setEpics((prev) => [...prev, row].sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title)));
    resetCreateForm();
    toast.success(`Created ${row.title}`);
  };

  const patchEpic = async (
    epic: WorkEpicRow,
    patch: Record<string, unknown>,
    success: string,
    reopen?: EditField,
  ) => {
    setSaving(true);
    const { data, error } = await supabase
      .from('work_epics')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', epic.id)
      .select('*')
      .single();
    setSaving(false);
    if (error) {
      reportUiError('tasks', 'update_work_epic', error);
      toast.error(error.message || 'Failed to update epic');
      if (reopen) beginEdit(epic, reopen);
      return;
    }
    const row = data as WorkEpicRow;
    setEpics((prev) => prev.map((e) => (e.id === row.id ? row : e)));
    setEditingId(null);
    setEditingField(null);
    setDraft('');
    toast.success(success);
  };

  const saveEdit = async (epic: WorkEpicRow, field: EditField, value: string) => {
    if (field === 'title') {
      const next = value.trim();
      if (!next) {
        toast.error('Epic name can’t be empty');
        beginEdit(epic, 'title');
        return;
      }
      if (next === epic.title) {
        cancelEdit();
        return;
      }
      await patchEpic(epic, { title: next }, 'Epic renamed', 'title');
      return;
    }

    if (field === 'description') {
      const next = value.trim();
      if (next === (epic.description ?? '').trim()) {
        cancelEdit();
        return;
      }
      await patchEpic(epic, { description: next || null }, 'Description updated', 'description');
      return;
    }

    if (field === 'value_note') {
      const next = value.trim();
      if (next === (epic.value_note ?? '').trim()) {
        cancelEdit();
        return;
      }
      await patchEpic(epic, { value_note: next || null }, 'Value note updated', 'value_note');
      return;
    }

    const nextDate = value.trim() || null;
    const prevDate = toDateInputValue(epic.target_date) || null;
    if (nextDate === prevDate) {
      cancelEdit();
      return;
    }
    await patchEpic(
      epic,
      { target_date: nextDate },
      nextDate ? 'Deadline set' : 'Deadline cleared',
      'deadline',
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Flag className="w-7 h-7 text-indigo-500" />
            Work epics
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            Plans that hold tasks — set a deadline so the board can schedule work toward it.
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <span
              className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}
            />
            {live ? 'Live' : 'Connecting…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" />
            Add epic
          </button>
          <Link
            to="/tasks/board"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Kanban className="w-4 h-4" />
            Eval board
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showInactive
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          {showInactive ? 'Showing all statuses' : 'Show done & archived'}
        </button>
        {unplacedOpen > 0 ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">
            {unplacedOpen} open task{unplacedOpen === 1 ? '' : 's'} with no epic
          </span>
        ) : null}
      </div>

      {createOpen ? (
        <form
          className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800 p-4 sm:p-5 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submitCreate();
          }}
        >
          <p className="text-sm font-medium text-gray-900 dark:text-white">New work epic</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Name</span>
              <input
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                className={`${inputClass} mt-1`}
                placeholder="e.g. Redwoods"
                autoFocus
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Goal</span>
              <textarea
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                rows={2}
                className={`${inputClass} mt-1 resize-y`}
                placeholder="What this block of work covers"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Value note</span>
              <input
                value={createValueNote}
                onChange={(e) => setCreateValueNote(e.target.value)}
                className={`${inputClass} mt-1`}
                placeholder="Why this plan — $ / goals / network"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Deadline</span>
              <input
                type="date"
                value={createDeadline}
                onChange={(e) => setCreateDeadline(e.target.value)}
                className={`${inputClass} mt-1`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Score</span>
              <select
                value={createScore}
                onChange={(e) => setCreateScore(e.target.value === '' ? '' : Number(e.target.value))}
                className={`${inputClass} mt-1`}
              >
                <option value="">None yet</option>
                {([1, 2, 3, 4, 5] as const).map((n) => (
                  <option key={n} value={n}>
                    {n} · {STRATEGY_SCORE_LABELS[n]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create epic
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={resetCreateForm}
              className="px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">
          No epics yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((epic) => {
            const deadline = formatDeadline(epic.target_date);
            const deadlinePast = isDeadlinePast(epic.target_date);
            const editingTitle = editingId === epic.id && editingField === 'title';
            const editingDesc = editingId === epic.id && editingField === 'description';
            const editingDeadline = editingId === epic.id && editingField === 'deadline';
            const editingValue = editingId === epic.id && editingField === 'value_note';
            const scoreLabel =
              epic.strategy_score != null
                ? `${epic.strategy_score} · ${STRATEGY_SCORE_LABELS[epic.strategy_score] ?? 'Score'}`
                : 'Set score';
            return (
              <li
                key={epic.id}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {editingTitle ? (
                        <input
                          value={draft}
                          disabled={saving}
                          autoFocus
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void saveEdit(epic, 'title', draft);
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEdit();
                            }
                          }}
                          onBlur={() => {
                            if (skipBlurSave.current) {
                              skipBlurSave.current = false;
                              return;
                            }
                            void saveEdit(epic, 'title', draft);
                          }}
                          className={`${inputClass} max-w-md font-semibold`}
                          aria-label="Epic name"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(epic, 'title')}
                          className="text-left text-base font-semibold text-gray-900 dark:text-white hover:text-indigo-700 dark:hover:text-indigo-300 rounded px-0.5 -mx-0.5"
                          title="Click to rename"
                        >
                          {epic.title}
                        </button>
                      )}
                      <select
                        value={epic.status}
                        disabled={saving}
                        onChange={(e) => {
                          const status = e.target.value as WorkEpicStatus;
                          if (status === epic.status) return;
                          void patchEpic(epic, { status }, `Status → ${status}`);
                        }}
                        className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize border-0 cursor-pointer ${STATUS_PILL[epic.status]}`}
                        aria-label="Epic status"
                      >
                        {WORK_EPIC_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <select
                        value={epic.strategy_score ?? ''}
                        disabled={saving}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const strategy_score = raw === '' ? null : Number(raw);
                          if (strategy_score === epic.strategy_score) return;
                          void patchEpic(
                            epic,
                            { strategy_score },
                            strategy_score == null ? 'Score cleared' : `Score → ${strategy_score}`,
                          );
                        }}
                        className="px-2 py-0.5 rounded-md text-[11px] font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 cursor-pointer"
                        aria-label="Epic strategy score"
                      >
                        <option value="">{scoreLabel === 'Set score' ? 'Set score' : 'Clear score'}</option>
                        {([1, 2, 3, 4, 5] as const).map((n) => (
                          <option key={n} value={n}>
                            {n} · {STRATEGY_SCORE_LABELS[n]}
                          </option>
                        ))}
                      </select>
                      {editingDeadline ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-[11px] font-medium text-gray-500">Deadline</span>
                          <input
                            type="date"
                            value={draft}
                            disabled={saving}
                            autoFocus
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void saveEdit(epic, 'deadline', draft);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEdit();
                              }
                            }}
                            onBlur={() => {
                              if (skipBlurSave.current) {
                                skipBlurSave.current = false;
                                return;
                              }
                              void saveEdit(epic, 'deadline', draft);
                            }}
                            className={`${inputClass} w-auto py-1 text-xs`}
                            aria-label="Epic deadline"
                          />
                          <button
                            type="button"
                            disabled={saving}
                            onMouseDown={() => {
                              skipBlurSave.current = true;
                            }}
                            onClick={() => void saveEdit(epic, 'deadline', '')}
                            className="text-[11px] text-gray-500 hover:text-rose-600"
                          >
                            Clear
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => beginEdit(epic, 'deadline')}
                          className={`text-xs rounded px-1.5 py-0.5 ${
                            deadlinePast
                              ? 'font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40'
                              : deadline
                                ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                : 'text-indigo-600 dark:text-indigo-400 hover:underline'
                          }`}
                          title="Click to set deadline"
                        >
                          {deadline
                            ? `Deadline ${deadline}${deadlinePast ? ' · past' : ''}`
                            : 'Set deadline'}
                        </button>
                      )}
                      <Link
                        to={`/tasks/board?epic=${epic.id}`}
                        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Open on board
                      </Link>
                    </div>
                    {editingDesc ? (
                      <textarea
                        value={draft}
                        disabled={saving}
                        autoFocus
                        rows={3}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEdit();
                          }
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                            e.preventDefault();
                            void saveEdit(epic, 'description', draft);
                          }
                        }}
                        onBlur={() => {
                          if (skipBlurSave.current) {
                            skipBlurSave.current = false;
                            return;
                          }
                          void saveEdit(epic, 'description', draft);
                        }}
                        className={`${inputClass} resize-y leading-relaxed`}
                        aria-label="Epic description"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => beginEdit(epic, 'description')}
                        className="block w-full text-left text-sm text-gray-600 dark:text-gray-300 leading-relaxed hover:text-indigo-700 dark:hover:text-indigo-300 rounded px-0.5 -mx-0.5"
                        title="Click to edit description"
                      >
                        {epic.description?.trim() || 'No goal summary yet — click to add.'}
                      </button>
                    )}
                    {editingValue ? (
                      <textarea
                        value={draft}
                        disabled={saving}
                        autoFocus
                        rows={2}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEdit();
                          }
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                            e.preventDefault();
                            void saveEdit(epic, 'value_note', draft);
                          }
                        }}
                        onBlur={() => {
                          if (skipBlurSave.current) {
                            skipBlurSave.current = false;
                            return;
                          }
                          void saveEdit(epic, 'value_note', draft);
                        }}
                        className={`${inputClass} resize-y leading-relaxed`}
                        aria-label="Epic value note"
                        placeholder="Why this plan — $ / goals / network"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => beginEdit(epic, 'value_note')}
                        className="block w-full text-left text-xs text-gray-500 dark:text-gray-400 leading-relaxed hover:text-indigo-700 dark:hover:text-indigo-300 rounded px-0.5 -mx-0.5"
                        title="Click to edit value note"
                      >
                        {epic.value_note?.trim() || 'No value note yet — click to add why this plan.'}
                      </button>
                    )}
                  </div>
                  <div className="shrink-0 flex sm:flex-col gap-3 sm:gap-1 sm:text-right sm:min-w-[7.5rem]">
                    <div>
                      <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white leading-none">
                        {epic.openCount}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">open</p>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 sm:mt-1">
                      <span className="tabular-nums">{epic.totalCount}</span> total
                      {epic.doneCount > 0 ? (
                        <>
                          {' '}
                          · <span className="tabular-nums">{epic.doneCount}</span> done
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default WorkEpicsView;
